import React, { useState } from 'react';
import { BrandProfileCard, BrandProfileCardProps } from './BrandProfileCard';
import { FormatProfileCard, FormatProfileCardProps } from './FormatProfileCard';
import { AddBrandProfileModal } from './AddBrandProfileModal';
import { AddFormatProfileModal } from './AddFormatProfileModal';
import { Button } from './ui/Button';
import { Plus, Search, Filter } from 'lucide-react';
import { Input } from './ui/Input';
import { cn } from '../lib/utils';
type ProfileTab = 'brands' | 'formats';
// Mock Data - einfachbank focused
const MOCK_BRAND_PROFILES: BrandProfileCardProps[] = [{
  id: '1',
  brandName: 'einfachbank Professional',
  industry: 'Banking & Finance',
  targetAudience: 'Financially savvy adults 30-55, professionals seeking transparent banking',
  brandVoice: 'Professional, trustworthy, transparent, educational',
  keyMessages: ['Transparency', 'No Hidden Fees', 'Financial Empowerment'],
  swipefileReferences: [{
    id: 'ref1',
    title: 'Understanding Your Credit Score',
    thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref2',
    title: 'Banking Fees Explained',
    thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref3',
    title: 'First Time Home Buyer Guide',
    thumbnail: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=60'
  }]
}, {
  id: '2',
  brandName: 'einfachbank Gen Z',
  industry: 'Banking & Finance',
  targetAudience: 'Gen Z 18-28, first-time banking customers, digital natives',
  brandVoice: 'Casual, authentic, relatable, educational but fun',
  keyMessages: ['Banking Made Simple', 'No BS', 'Your First Bank'],
  swipefileReferences: [{
    id: 'ref4',
    title: '5 Banking Mistakes Gen Z Makes',
    thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref5',
    title: 'Savings Hacks Nobody Tells You',
    thumbnail: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref6',
    title: "What Banks Don't Want You to Know",
    thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60'
  }]
}];
const MOCK_FORMAT_PROFILES: FormatProfileCardProps[] = [{
  id: '1',
  formatName: 'Talking Head',
  brandName: 'einfachbank Professional',
  description: 'Direct-to-camera format with banking expert explaining concepts professionally. Clean background, professional attire.',
  duration: '60-90 seconds',
  style: 'Professional, Authoritative',
  keyElements: ['Eye contact', 'Clean office background', 'Subtitles', 'Professional lighting'],
  swipefileReferences: [{
    id: 'ref1',
    title: 'Understanding Your Credit Score',
    thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref2',
    title: 'First Time Home Buyer Guide',
    thumbnail: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=60'
  }]
}, {
  id: '2',
  formatName: 'Whiteboard Session',
  brandName: 'einfachbank Gen Z',
  description: 'Hand-drawn whiteboard animations explaining banking concepts. Fast-paced, visual, and engaging for younger audience.',
  duration: '45-75 seconds',
  style: 'Casual, Educational',
  keyElements: ['Hand-drawn visuals', 'Quick reveals', 'Bold colors', 'Simple illustrations'],
  swipefileReferences: [{
    id: 'ref3',
    title: '5 Banking Mistakes Gen Z Makes',
    thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref4',
    title: 'How Compound Interest Actually Works',
    thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60'
  }]
}, {
  id: '3',
  formatName: 'Sketch Explainer',
  brandName: 'einfachbank Professional',
  description: 'Clean sketch-based visuals with annotations. Professional approach to breaking down complex banking topics.',
  duration: '90-120 seconds',
  style: 'Clean, Professional',
  keyElements: ['Minimal sketches', 'Annotations', 'Data visualization', 'Smooth transitions'],
  swipefileReferences: [{
    id: 'ref5',
    title: 'Banking Fees Explained',
    thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60'
  }]
}, {
  id: '4',
  formatName: 'Casual Talking Head',
  brandName: 'einfachbank Gen Z',
  description: 'Relaxed, conversational direct-to-camera. Casual setting, relatable tone, like talking to a friend about money.',
  duration: '30-60 seconds',
  style: 'Casual, Authentic',
  keyElements: ['Natural lighting', 'Casual background', 'Handheld feel', 'Conversational tone'],
  swipefileReferences: [{
    id: 'ref6',
    title: 'Savings Hacks Nobody Tells You',
    thumbnail: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=800&auto=format&fit=crop&q=60'
  }, {
    id: 'ref7',
    title: "What Banks Don't Want You to Know",
    thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60'
  }]
}];
export function Profiles() {
  const [activeTab, setActiveTab] = useState<ProfileTab>('brands');
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isAddFormatModalOpen, setIsAddFormatModalOpen] = useState(false);
  const handleAddBrandProfile = (profile: any) => {
    console.log('New brand profile:', profile);
    // TODO: Add to brand profiles list
  };
  const handleAddFormatProfile = (profile: any) => {
    console.log('New format profile:', profile);
    // TODO: Add to format profiles list
  };
  const tabs = [{
    id: 'brands' as ProfileTab,
    label: 'Brand Profiles',
    count: MOCK_BRAND_PROFILES.length
  }, {
    id: 'formats' as ProfileTab,
    label: 'Format Profiles',
    count: MOCK_FORMAT_PROFILES.length
  }];
  return <>
      <div className="flex-1 h-full flex flex-col bg-gray-50/50" data-id="element-545">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b px-8 py-6" data-id="element-546">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-547">
            <div data-id="element-548">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-549">Profiles</h1>
              <p className="text-gray-500 mt-1" data-id="element-550">
                Manage brand identities and content formats
              </p>
            </div>
            <Button className="bg-[#139191] hover:bg-[#139191]/90 text-white shadow-md gap-2" onClick={() => activeTab === 'brands' ? setIsAddBrandModalOpen(true) : setIsAddFormatModalOpen(true)} data-id="element-551">
              <Plus className="h-4 w-4" data-id="element-552" />
              {activeTab === 'brands' ? 'New Brand Profile' : 'New Format Profile'}
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-4 border-b border-gray-200" data-id="element-553">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('px-4 py-2.5 text-sm font-medium transition-colors relative', activeTab === tab.id ? 'text-[#139191]' : 'text-gray-600 hover:text-gray-900')} data-id="element-554">
                {tab.label}
                <span className={cn('ml-2 px-2 py-0.5 rounded-full text-xs', activeTab === tab.id ? 'bg-[#139191]/10 text-[#139191]' : 'bg-gray-100 text-gray-600')} data-id="element-555">
                  {tab.count}
                </span>
                {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#139191]" data-id="element-556" />}
              </button>)}
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-4 items-center" data-id="element-557">
            <div className="relative flex-1 w-full" data-id="element-558">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-559" />
              <Input placeholder={`Search ${activeTab === 'brands' ? 'brand' : 'format'} profiles...`} className="pl-10 bg-white border-gray-200 focus-visible:ring-[#139191]" data-id="element-560" />
            </div>
            <Button variant="outline" className="w-full md:w-auto gap-2 border-gray-200 text-gray-600 hover:text-[#139191] hover:border-[#139191]/30" data-id="element-561">
              <Filter className="h-4 w-4" data-id="element-562" />
              Filter
            </Button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="flex-1 overflow-auto p-8" data-id="element-563">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8" data-id="element-564">
            {activeTab === 'brands' && MOCK_BRAND_PROFILES.map(profile => <BrandProfileCard key={profile.id} {...profile} data-id="element-565" />)}
            {activeTab === 'formats' && MOCK_FORMAT_PROFILES.map(profile => <FormatProfileCard key={profile.id} {...profile} data-id="element-566" />)}
          </div>
        </div>
      </div>

      {/* Add Brand Profile Modal */}
      <AddBrandProfileModal isOpen={isAddBrandModalOpen} onClose={() => setIsAddBrandModalOpen(false)} onSubmit={handleAddBrandProfile} data-id="element-567" />

      {/* Add Format Profile Modal */}
      <AddFormatProfileModal isOpen={isAddFormatModalOpen} onClose={() => setIsAddFormatModalOpen(false)} onSubmit={handleAddFormatProfile} data-id="element-568" />
    </>;
}